import React, { useState } from 'react';
import { css } from '@emotion/css';
import {
    ConditionalCondition,
    ConditionalDefault,
    ConditionalOperator,
    CONDITIONAL_OPERATOR_LABELS,
    operatorRequiresValue,
} from '../../../models/templating/api/ConditionalDefaultModel';
import TemplateFormFieldModel from '../../../models/templating/api/TemplateFormFieldModel';
import {
    color_shades_dark,
    color_shades_light,
    color_shades_lighter,
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_variants_ocean_light_1,
    color_colors_decline,
    color_variants_decline_light_1,
    color_shades_medium,
    color_shades_darker,
} from '../../../constants/colors';
import { validateConditionalDefaults } from '../../../utils/conditionalDefaultUtils';
import DropDownSelectField from '../../../components/forms/DropDownSelectField';

const addIcon = require('../../../content/images/add-circle.svg');
const deleteIcon = require('../../../content/images/menu-item-icons/bin.svg');

interface ConditionalDefaultEditorProps {
    /** All fields in the section, used to build the "when field" dropdown. */
    allFields: TemplateFormFieldModel[]
    /** The field being edited */
    currentField: TemplateFormFieldModel
    /** Current conditional default config */
    value: ConditionalDefault | undefined
    onChange: (value: ConditionalDefault | undefined) => void
    /** Called when user wants to copy this entire conditional defaults block to another field */
    onCopyTo?: (targetFieldId: string) => void
}

const ALL_OPERATORS: ConditionalOperator[] = [
    'equals',
    'notEquals',
    'contains',
    'startsWith',
    'isEmpty',
    'isNotEmpty',
    'greaterThan',
    'lessThan',
];

const operatorOptions = ALL_OPERATORS.map(op => ({
    value: op,
    label: CONDITIONAL_OPERATOR_LABELS[op],
}));

const ConditionalDefaultEditor: React.FC<ConditionalDefaultEditorProps> = (props) => {
    const { allFields, currentField, value, onChange } = props;

    const isEnabled = value !== undefined;
    const [isCopyMenuOpen, setIsCopyMenuOpen] = useState(false);
    const [copyMenuSearch, setCopyMenuSearch] = useState('');

    const handleToggle = () => {
        if (!isEnabled) {
            onChange({
                conditions: [buildEmptyCondition()],
                otherwise: '',
            });
        } else {
            onChange(undefined);
        }
    };

    const buildEmptyCondition = (): ConditionalCondition => ({
        fieldAssociatedVariable: '',
        operator: 'equals',
        value: '',
        defaultValue: '',
    });

    const handleAddCondition = () => {
        onChange({
            ...value,
            conditions: [...(value?.conditions ?? []), buildEmptyCondition()],
        });
    };

    const handleRemoveCondition = (index: number) => {
        const updated = (value?.conditions ?? []).filter((_, i) => i !== index);
        onChange({ ...value, conditions: updated });
    };

    const handleDuplicateCondition = (index: number) => {
        const conditions = value?.conditions ?? [];
        const copy: ConditionalCondition = { ...conditions[index] };
        const updated = [
            ...conditions.slice(0, index + 1),
            copy,
            ...conditions.slice(index + 1),
        ];
        onChange({ ...value, conditions: updated });
    };

    const handleConditionChange = (index: number, patch: Partial<ConditionalCondition>) => {
        const updated = (value?.conditions ?? []).map((c, i) =>
            i === index ? { ...c, ...patch } : c
        );
        onChange({ ...value, conditions: updated });
    };

    const handleOtherwiseChange = (val: string) => {
        onChange({ ...value, conditions: value?.conditions ?? [], otherwise: val });
    };

    const currentFieldIndex = allFields.findIndex(
        f => f.id === currentField.id || (f.name && f.name === currentField.name)
    );

    const fieldOptions = allFields
        .filter(f => {
            const isSelf = f.id === currentField.id || (f.name && f.name === currentField.name);
            return !isSelf && !!f.associatedVariable;
        })
        .map(f => {
            const fieldIndex = allFields.indexOf(f);
            const isAfter = currentFieldIndex !== -1 && fieldIndex > currentFieldIndex;
            return {
                value: f.associatedVariable,
                label: isAfter
                    ? `${f.title ?? f.associatedVariable} ⚠`
                    : (f.title ?? f.associatedVariable),
            };
        });

    const getValueOptionsForField = (associatedVariable: string) => {
        const field = allFields.find(f => f.associatedVariable === associatedVariable);
        if (!field?.options?.length) return [];
        return field.options.map(o => ({ value: o.value, label: o.label ?? o.value }));
    };

    const validationErrors = validateConditionalDefaults(value);
    const conditions = value?.conditions ?? [];

    return (
        <div className={containerStyle}>
            {/* Header row */}
            <div className="cde-header">
                <div className="cde-header-left">
                    <p className="cde-title">Conditional Default Value</p>
                    <p className="cde-subtitle">
                        {isEnabled
                            ? 'The default value is controlled by the conditions below.'
                            : "Override the default value based on another field's value."}
                    </p>
                </div>
                <div className="cde-header-actions">
                <button
                    type="button"
                    className={`cde-toggle-btn ${isEnabled ? 'active' : ''}`}
                    onClick={handleToggle}
                >
                    {isEnabled ? 'Disable' : 'Enable'}
                </button>
                {isEnabled && props.onCopyTo && (
                    <div className="cde-copy-menu-container">
                        <button
                            type="button"
                            className={`cde-copy-menu-btn ${isCopyMenuOpen ? 'open' : ''}`}
                            onClick={() => {
                                setIsCopyMenuOpen(!isCopyMenuOpen);
                                setCopyMenuSearch('');
                            }}
                            title="Copy this conditional default block to another field"
                        >
                            Copy to field...
                        </button>
                        {isCopyMenuOpen && (
                            <div className="cde-copy-menu">
                                <div className="cde-copy-menu-search">
                                    <input
                                        type="text"
                                        className="cde-copy-menu-search-input"
                                        placeholder="Search fields..."
                                        value={copyMenuSearch}
                                        onChange={e => setCopyMenuSearch(e.target.value)}
                                        autoFocus
                                        onClick={e => e.stopPropagation()}
                                    />
                                </div>
                                {(() => {
                                    const otherFields = allFields.filter(
                                        f => f.id !== currentField.id && !(f.name && f.name === currentField.name)
                                    );
                                    const filtered = copyMenuSearch.trim()
                                        ? otherFields.filter(f => {
                                            const label = (f.title ?? f.associatedVariable ?? f.id ?? '').toLowerCase();
                                            return label.includes(copyMenuSearch.trim().toLowerCase());
                                        })
                                        : otherFields;
                                    if (filtered.length === 0) {
                                        return <p className="cde-copy-menu-empty">
                                            {otherFields.length === 0 ? 'No other fields available' : 'No fields match your search'}
                                        </p>;
                                    }
                                    return filtered.map(f => (
                                        <button
                                            key={f.id ?? f.associatedVariable}
                                            type="button"
                                            className="cde-copy-menu-item"
                                            onClick={() => {
                                                props.onCopyTo(f.id);
                                                setIsCopyMenuOpen(false);
                                                setCopyMenuSearch('');
                                            }}
                                        >
                                            {f.title ?? f.associatedVariable ?? f.id}
                                        </button>
                                    ));
                                })()}
                            </div>
                        )}
                    </div>
                )}
                </div>
            </div>

            {isEnabled && (
                <div className="cde-body">

                    {/* Condition cards */}
                    {conditions.map((condition, index) => {
                        const valueOptions = getValueOptionsForField(condition.fieldAssociatedVariable);
                        const needsValue = operatorRequiresValue(condition.operator);
                        const isFirst = index === 0;

                        return (
                            <div key={index} className="cde-card">
                                {/* Card header: IF / ELSE IF + delete */}
                                 <div className="cde-card-header">
                                     <span className={`cde-badge ${isFirst ? 'if' : 'elseif'}`}>
                                         {isFirst ? 'IF' : 'ELSE IF'}
                                     </span>
                                     <div className="cde-card-header-actions">
                                         <button
                                             type="button"
                                             className="cde-delete-btn"
                                             onClick={() => handleRemoveCondition(index)}
                                            title="Remove condition"
                                        >
                                            <img src={deleteIcon} alt="Delete" />
                                        </button>
                                    </div>
                                </div>

                                {/* Condition inputs: Field, Operator, Value stacked */}
                                 <div className="cde-card-body">
                                     <div className="cde-field-group">
                                         <label className="cde-field-label">Field</label>
                                         <DropDownSelectField
                                             size="medium"
                                             searchable={true}
                                             placeholder="Select a field…"
                                             value={condition.fieldAssociatedVariable || null}
                                             options={fieldOptions}
                                             onChange={opt => handleConditionChange(index, {
                                                 fieldAssociatedVariable: opt ? String(opt.value) : '',
                                                 value: '',
                                             })}
                                         />
                                     </div>

                                    <div className="cde-field-group">
                                        <label className="cde-field-label">Operator</label>
                                        <select
                                            className="cde-select"
                                            value={condition.operator}
                                            onChange={e => handleConditionChange(index, { operator: e.target.value as ConditionalOperator })}
                                        >
                                            {operatorOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {needsValue && (
                                        <div className="cde-field-group">
                                            <label className="cde-field-label">Value</label>
                                            {valueOptions.length > 0 ? (
                                                <select
                                                    className="cde-select"
                                                    value={condition.value ?? ''}
                                                    onChange={e => handleConditionChange(index, { value: e.target.value })}
                                                >
                                                    <option value="">Select a value…</option>
                                                    {valueOptions.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    className="cde-input"
                                                    type="text"
                                                    value={condition.value ?? ''}
                                                    placeholder='e.g. "OLO" or "true"'
                                                    onChange={e => handleConditionChange(index, { value: e.target.value })}
                                                />
                                            )}
                                        </div>
                                    )}

                                    {/* Then */}
                                    <div className="cde-then-row">
                                        <span className="cde-then-arrow">→</span>
                                        <div className="cde-field-group cde-field-group-then">
                                            <label className="cde-field-label">Use this default value</label>
                                            <input
                                                className="cde-input"
                                                type="text"
                                                value={condition.defaultValue ?? ''}
                                                placeholder="Default value to apply"
                                                onChange={e => handleConditionChange(index, { defaultValue: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Add condition button */}
                    <button type="button" className="cde-add-btn" onClick={handleAddCondition}>
                        <img src={addIcon} alt="" />
                        {conditions.length === 0 ? 'Add a Condition' : 'Add Else If Condition'}
                    </button>

                    {/* Fallback row */}
                    <div className="cde-fallback-section">
                        <div className="cde-fallback-header">
                            <span className="cde-badge otherwise">OTHERWISE</span>
                            <p className="cde-fallback-hint">Used when none of the conditions above match.</p>
                        </div>
                        <div className="cde-field-group">
                            <label className="cde-field-label">Fallback default value</label>
                            <input
                                className="cde-input"
                                type="text"
                                value={value?.otherwise ?? ''}
                                placeholder="Leave empty for no default"
                                onChange={e => handleOtherwiseChange(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Preview */}
                    {conditions.length > 0 && (
                        <div className="cde-preview">
                            <p className="cde-preview-title">Logic Preview</p>
                            {conditions.map((condition, index) => {
                                const fieldLabel = fieldOptions.find(f => f.value === condition.fieldAssociatedVariable)?.label
                                    ?? condition.fieldAssociatedVariable
                                    ?? '(no field)';
                                const operatorLabel = CONDITIONAL_OPERATOR_LABELS[condition.operator] ?? condition.operator;
                                const comparisonStr = operatorRequiresValue(condition.operator)
                                    ? ` "${condition.value ?? ''}"`
                                    : '';
                                const prefix = index === 0 ? 'If' : 'Else if';
                                return (
                                    <div key={index} className="cde-preview-row">
                                        <span className="cde-preview-condition">
                                            {prefix} <strong>{fieldLabel}</strong> {operatorLabel}{comparisonStr}
                                        </span>
                                        <span className="cde-preview-sep">→</span>
                                        <span className="cde-preview-value">
                                            {condition.defaultValue ? `"${condition.defaultValue}"` : <em>(empty string)</em>}
                                        </span>
                                    </div>
                                );
                            })}
                            <div className="cde-preview-row cde-preview-otherwise">
                                <span className="cde-preview-condition">Otherwise</span>
                                <span className="cde-preview-sep">→</span>
                                <span className="cde-preview-value">
                                    {value?.otherwise
                                        ? `"${value.otherwise}"`
                                        : <em>(no default)</em>}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Validation errors */}
                    {validationErrors.length > 0 && (
                        <div className="cde-errors">
                            {validationErrors.map((err, i) => (
                                <p key={i} className="cde-error-item">{err}</p>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const containerStyle = css`
    margin-top: 8px;
    border-top: 1px solid ${color_shades_dark};
    padding-top: 20px;

    /* ── Header ── */
    .cde-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 0;
    }

    .cde-header-left {
        flex: 1;
    }

    .cde-header-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }

    .cde-title {
        font-size: 14px;
        font-weight: 600;
        color: ${color_text_default};
        margin: 0 0 2px 0;
    }

    .cde-subtitle {
        font-size: 12px;
        color: ${color_text_light};
        margin: 0;
        line-height: 1.5;
    }

    .cde-subtitle-note {
        font-style: italic;
    }

    .cde-toggle-btn {
        flex-shrink: 0;
        font-size: 12px;
        font-weight: 600;
        font-family: Muli, sans-serif;
        padding: 6px 14px;
        border-radius: 6px;
        border: 1px solid ${color_shades_dark};
        background: white;
        color: ${color_text_light};
        cursor: pointer;
        transition: all 0.15s;
        white-space: nowrap;

        &:hover {
            border-color: ${color_colors_ocean};
            color: ${color_colors_ocean};
        }

        &.active {
            background: ${color_variants_ocean_light_1};
            border-color: ${color_colors_ocean};
            color: ${color_colors_ocean};
        }
    }

    /* ── Body ── */
    .cde-body {
        margin-top: 16px;
    }

    /* ── Condition card ── */
    .cde-card {
        border: 1px solid ${color_shades_dark};
        border-radius: 8px;
        background: white;
        margin-bottom: 10px;
        overflow: visible;
    }

    .cde-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 12px 6px 12px;
        border-bottom: 1px solid ${color_shades_light};
        background: ${color_shades_lighter};
        border-radius: 8px 8px 0 0;
    }

    /* ── Badge ── */
    .cde-badge {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        padding: 2px 8px;
        border-radius: 3px;

        &.if {
            background: ${color_colors_ocean};
            color: white;
        }
        &.elseif {
            background: ${color_shades_darker};
            color: white;
        }
        &.otherwise {
            background: ${color_shades_dark};
            color: ${color_text_default};
        }
    }

    .cde-delete-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 2px 4px;
        border-radius: 4px;
        opacity: 0.45;
        transition: opacity 0.15s, background 0.15s;
        display: flex;
        align-items: center;

        &:hover {
            opacity: 1;
            background: ${color_variants_decline_light_1};
        }

        img {
            width: 14px;
            height: 14px;
            display: block;
        }
    }

    .cde-card-body {
        padding: 12px 14px 14px 14px;
    }

    /* ── Field groups (label + control) ── */
    .cde-field-group {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-bottom: 10px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .cde-field-label {
        font-size: 11px;
        font-weight: 600;
        color: ${color_text_light};
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-family: Muli, sans-serif;
    }

    /* Native select styled to match app inputs */
    .cde-select {
        appearance: none;
        -webkit-appearance: none;
        width: 100%;
        height: 44px;
        padding: 0 36px 0 12px;
        font-family: Muli, sans-serif;
        font-size: 13px;
        color: ${color_text_default};
        background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23666'/%3E%3C/svg%3E") no-repeat right 12px center;
        border: 1px solid ${color_shades_dark};
        border-radius: 8px;
        cursor: pointer;
        outline: none;
        transition: border-color 0.15s;

        &:focus {
            border-color: ${color_colors_ocean};
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        option {
            font-size: 13px;
        }
    }

    /* Native text input styled to match app inputs */
    .cde-input {
        width: 100%;
        height: 44px;
        padding: 0 12px;
        font-family: Muli, sans-serif;
        font-size: 13px;
        color: ${color_text_default};
        background: white;
        border: 1px solid ${color_shades_dark};
        border-radius: 8px;
        outline: none;
        transition: border-color 0.15s;
        box-sizing: border-box;

        &::placeholder {
            color: ${color_shades_darker};
            font-style: italic;
        }

        &:focus {
            border-color: ${color_colors_ocean};
        }
    }

    /* ── Then row ── */
    .cde-then-row {
        display: flex;
        align-items: flex-end;
        gap: 10px;
        padding-top: 4px;
        border-top: 1px dashed ${color_shades_medium};
        margin-top: 2px;
    }

    .cde-then-arrow {
        font-size: 18px;
        color: ${color_colors_ocean};
        flex-shrink: 0;
        padding-bottom: 10px;
        line-height: 1;
    }

    .cde-field-group-then {
        flex: 1;
        margin-bottom: 0;
    }

    /* ── Add button ── */
    .cde-add-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        background: none;
        border: 1px dashed ${color_colors_ocean};
        color: ${color_colors_ocean};
        font-family: Muli, sans-serif;
        font-size: 12px;
        font-weight: 600;
        padding: 7px 14px;
        border-radius: 6px;
        cursor: pointer;
        transition: background 0.15s;
        margin-bottom: 16px;
        margin-top: 2px;

        &:hover {
            background: ${color_variants_ocean_light_1};
        }

        img {
            width: 14px;
            height: 14px;
        }
    }

    /* ── Fallback / Otherwise section ── */
    .cde-fallback-section {
        border: 1px solid ${color_shades_dark};
        border-radius: 8px;
        background: white;
        padding: 10px 14px 14px 14px;
        margin-bottom: 12px;
    }

    .cde-fallback-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 10px;
    }

    .cde-fallback-hint {
        font-size: 11px;
        color: ${color_text_light};
        margin: 0;
        font-style: italic;
    }

    /* ── Preview ── */
    .cde-preview {
        padding: 10px 14px;
        background: ${color_shades_lighter};
        border: 1px solid ${color_shades_medium};
        border-radius: 8px;
        margin-bottom: 10px;
    }

    .cde-preview-title {
        font-size: 10px;
        font-weight: 700;
        color: ${color_text_light};
        text-transform: uppercase;
        letter-spacing: 0.6px;
        margin: 0 0 6px 0;
    }

    .cde-preview-row {
        display: flex;
        align-items: baseline;
        gap: 6px;
        font-size: 12px;
        color: ${color_text_default};
        padding: 2px 0;
        flex-wrap: wrap;

        strong {
            color: ${color_colors_ocean};
        }
    }

    .cde-preview-condition {
        word-break: break-word;
    }

    .cde-preview-sep {
        color: ${color_shades_darker};
        font-size: 12px;
        flex-shrink: 0;
    }

    .cde-preview-otherwise {
        margin-top: 5px;
        padding-top: 5px;
        border-top: 1px solid ${color_shades_light};
        color: ${color_shades_darker};
    }

    .cde-preview-value {
        font-family: monospace;
        font-size: 11px;
        background: ${color_shades_light};
        padding: 1px 5px;
        border-radius: 3px;
        word-break: break-all;
        white-space: normal;

        em {
            color: ${color_text_light};
            font-style: italic;
        }
    }

    /* ── Copy-to-field menu ── */
    .cde-copy-menu-container {
        position: relative;
        flex-shrink: 0;
    }

    .cde-copy-menu-btn {
        flex-shrink: 0;
        font-size: 12px;
        font-weight: 600;
        font-family: Muli, sans-serif;
        padding: 6px 14px;
        border-radius: 6px;
        border: 1px solid ${color_shades_dark};
        background: white;
        color: ${color_text_light};
        cursor: pointer;
        transition: all 0.15s;
        white-space: nowrap;

        &:hover {
            border-color: ${color_colors_ocean};
            color: ${color_colors_ocean};
        }

        &.open {
            border-color: ${color_colors_ocean};
            color: ${color_colors_ocean};
            background: ${color_variants_ocean_light_1};
        }
    }

    .cde-copy-menu {
        position: absolute;
        top: calc(100% + 4px);
        right: 0;
        z-index: 100;
        background: white;
        border: 1px solid ${color_shades_dark};
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        min-width: 220px;
        max-height: 280px;
        overflow-y: auto;
        padding: 4px 0;
    }

    .cde-copy-menu-search {
        padding: 6px 8px;
        border-bottom: 1px solid ${color_shades_light};
        position: sticky;
        top: 0;
        background: white;
        z-index: 1;
    }

    .cde-copy-menu-search-input {
        width: 100%;
        height: 32px;
        padding: 0 10px;
        font-family: Muli, sans-serif;
        font-size: 12px;
        color: ${color_text_default};
        background: ${color_shades_lighter};
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        outline: none;
        box-sizing: border-box;
        transition: border-color 0.15s;

        &::placeholder {
            color: ${color_shades_darker};
            font-style: italic;
        }

        &:focus {
            border-color: ${color_colors_ocean};
            background: white;
        }
    }

    .cde-copy-menu-item {
        display: block;
        width: 100%;
        text-align: left;
        background: none;
        border: none;
        padding: 8px 14px;
        font-family: Muli, sans-serif;
        font-size: 13px;
        color: ${color_text_default};
        cursor: pointer;
        transition: background 0.1s;

        &:hover {
            background: ${color_variants_ocean_light_1};
            color: ${color_colors_ocean};
        }
    }

    .cde-copy-menu-empty {
        padding: 8px 14px;
        font-size: 12px;
        color: ${color_text_light};
        font-style: italic;
        margin: 0;
    }

    /* ── Errors ── */
    .cde-errors {
        padding: 8px 12px;
        background: ${color_variants_decline_light_1};
        border: 1px solid ${color_colors_decline};
        border-radius: 6px;
    }

    .cde-error-item {
        font-size: 12px;
        color: ${color_colors_decline};
        margin: 0 0 4px 0;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

export default ConditionalDefaultEditor;
